import { useCallback, type DragEvent } from 'react';
import type Konva from 'konva';
import type * as Y from 'yjs';
import type { CommandManager } from '../../../core/commands/CommandManager';
import { CreateShapeCommand } from '../../../core/commands/CreateShapeCommand';
import { CreateEdgeCommand } from '../../../core/commands/CreateEdgeCommand';
import type { DiagramType } from '../../../core/tools/interfaces/ITool';
import type { ToolRegistry } from '../../../core/tools/ToolRegistry';
import { getShapeManifest } from '../../../lib/diagram/registries';
import { createTableFromSchema } from '../../../domains/diagram/shapes/table/createTableFromSchema';
import { useDiagramStore } from '../../../stores/diagramStore';
import { Logger } from '../../../utils/logger';
import { createShapeDataBinding } from '@/domains/data-binding';
import { snapPointToGrid } from '../../../lib/diagram/gridSnap';
import {
  collectSupportedImageFilesForCanvasDrop,
  collectSupportedPdfFilesForCanvasDrop,
  extractImageUrlFromDataTransfer,
  extractFirstSupportedImageFileFromDataTransfer,
} from '../utils/canvasImageFiles';
import { CompositeCommand, type ICommand } from '../../../core/commands/ICommand';
import { CreateNetworkResourceCommand } from '../../../domains/network/commands/CreateNetworkResourceCommand';
import { createNetworkElementCommand } from '../../../domains/network/commands';
import {
  COMPASS_SUBTREE_MIME,
  type CompassSubtreeDragPayload,
} from '../../../domains/compass/components/panel/CompassFolderResourcesSection';
import { layoutDroppedSubtree } from '../../../domains/compass/canvas/dropSubtreeLayout';
import type { FrameManager } from '../../../core/frames/FrameManager';
import { ShapeMapAdapter } from '../../../lib/diagram/ShapeMapAdapter';
import { ShapePersistenceGateway } from '../../../lib/diagram/shapeGeometry';
import { DEFAULT_ORG_CHART_VIEW_MODE } from '../../../domains/orgchart/models/defaults';
import { resolveOrgShapeDefaultDimensions } from '../../../domains/orgchart/models/orgChartShapeVariants';

const canvasLogger = Logger.getLoggerWithPrefix('DiagramCanvasWithTransform');

function resolveOrgViewMode(doc: Y.Doc | null | undefined): 'compact' | 'full' {
  try {
    const settingsMap = doc?.getMap?.('diagramSettings');
    const settingsJson = settingsMap?.get?.('settings');
    if (!settingsJson || typeof settingsJson !== 'string') return DEFAULT_ORG_CHART_VIEW_MODE;
    const parsed = JSON.parse(settingsJson) as { orgchart?: { viewMode?: 'compact' | 'full' } };
    return parsed?.orgchart?.viewMode === 'full' ? 'full' : DEFAULT_ORG_CHART_VIEW_MODE;
  } catch {
    return DEFAULT_ORG_CHART_VIEW_MODE;
  }
}

export function useCanvasDropHandlers(input: {
  canWrite: boolean;
  stageRef: { current: Konva.Stage | null };
  doc: Y.Doc | null | undefined;
  commandManager: CommandManager;
  toolRegistry: ToolRegistry;
  frameManager?: FrameManager;
  onImageFileDrop?: (file: File, point: { x: number; y: number }) => void | Promise<void>;
  onImageFilesDrop?: (
    files: File[],
    point: { x: number; y: number },
    notice: { skippedOverSize: number; skippedOverMaxCount: number },
  ) => void | Promise<void>;
  onPdfFilesDrop?: (
    files: File[],
    point: { x: number; y: number },
    notice: { skippedOverMaxCount: number },
  ) => void | Promise<void>;
  onImageUrlDrop?: (url: string, point: { x: number; y: number }) => void | Promise<void>;
  onUnsupportedImageDrop?: () => void;
}): {
  handleDragOver: (e: DragEvent) => void;
  handleDrop: (e: DragEvent) => void;
} {
  const {
    canWrite,
    stageRef,
    doc,
    commandManager,
    toolRegistry,
    onImageFileDrop,
    onImageFilesDrop,
    onPdfFilesDrop,
    onImageUrlDrop,
    onUnsupportedImageDrop,
  } = input;
  void input.frameManager; // reserved for future frame-authored drops

  const handleRepositoryItemDrop = useCallback(
    (x: number, y: number, repoItem: { sourceId?: string; folderId?: string; recordId: string; data: Record<string, unknown>; originalData?: Record<string, unknown>, type: string }) => {
      if (!doc) return;
      const snappedPosition = snapPointToGrid({ x, y });

      // data = effective data (with local changes for display)
      // originalData = original repository data (for cachedRecord baseline)
      const effectiveData = repoItem.data;
      const originalData = repoItem.originalData ?? repoItem.data;

      // Get the name from effective data (includes any local changes)
      const name = (effectiveData.Name ?? effectiveData.name ?? 'Employee') as string;
      const title = (effectiveData.Title ?? effectiveData.title ?? effectiveData.role) as string | undefined;
      const department = (effectiveData.Department ?? effectiveData.department) as string | undefined;
      const email = (effectiveData.Email ?? effectiveData.email) as string | undefined;

      // Create a process shape with data binding
      const shapeId = `node_${Date.now()}_${Math.random().toString(36).substring(2, 9)}`;
      const manifest = getShapeManifest('org', 'org_person_text');
      const defaultDims = manifest?.defaultDimensions || { width: 120, height: 80 };

      // Create the data binding with proper structure
      // IMPORTANT: cachedRecord uses ORIGINAL data (not effective) for proper change detection
      // This ensures local changes in the central store aren't pruned when comparing
      const dataBinding = repoItem.sourceId
        ? createShapeDataBinding(repoItem.sourceId, repoItem.recordId, originalData)
        : null;
      const graphBinding =
        repoItem.type === 'process-graph-item'
          ? {
              kind: 'compass-process',
              folderId: repoItem.folderId ?? null,
              processId: repoItem.recordId,
            }
          : null;

      // Extract visual properties from effective data (includes local changes)
      // These will be applied to the new shape so it matches what's in the central store
      const fillColor = (effectiveData.fillColor ?? effectiveData.FillColor ?? '#FFFFFF') as string;
      const strokeColor = (effectiveData.strokeColor ?? effectiveData.StrokeColor ?? '#1E293B') as string;
      const textColor = (effectiveData.textColor ?? effectiveData.TextColor) as string | undefined;
      const description = (effectiveData.description ?? effectiveData.Description) as string | undefined;

      // Create shape with binding in a single Yjs transaction
      // Note: type must be 'flowchart-shape' for the renderer, with 'process' as the variant
      // IMPORTANT: dataBinding must be in props.dataBinding for tracking hooks to work
      const shapes = new ShapePersistenceGateway(new ShapeMapAdapter(doc.getMap('shapes')));
      doc.transact(() => {
        const newShape: Record<string, unknown> = {
          id: shapeId,
          x: snappedPosition.x,
          y: snappedPosition.y,
          w: defaultDims.width,
          h: defaultDims.height,
          width: defaultDims.width,
          height: defaultDims.height,
          fillColor,
          strokeColor,
          text: name,
        };
        let shapeAttrs: Record<string, unknown> = {};
        let shapeProps: Record<string, unknown> = {};

        if (repoItem.type === 'process-repository-item' || repoItem.type === 'process-graph-item')  {
          newShape.type = 'flowchart-shape';
          newShape.name = 'process';
          newShape.category = 'flowchart';
          shapeAttrs = {
            variant: 'process',
            category: 'flowchart',
            label: name,
            text: name,
          };
          shapeProps = {
            text: name,
          };
          if (dataBinding) {
            shapeProps.dataBinding = dataBinding;
          }
          if (graphBinding) {
            shapeProps.graphBinding = graphBinding;
            shapeAttrs.graphBinding = graphBinding;
          }
        } else if (repoItem.type === 'orgchart-repository-item') {
          newShape.type = 'org_person_text';
          newShape.name = name;
          shapeAttrs = {
            personId: repoItem.recordId,
            name,
            role: title ?? '',
            department,
            email,
          };
          shapeProps = {
            personId: repoItem.recordId,
            name,
            role: title ?? '',
            department,
            email,
          };
          if (dataBinding) {
            shapeProps.dataBinding = dataBinding;
          }
        }
        newShape.attrs = shapeAttrs;
        newShape.props = shapeProps;

        // Apply optional properties from effective data
        if (textColor) {
          newShape.textColor = textColor;
        }
        if (description !== undefined) {
          newShape.description = description;
          shapeAttrs.description = description;
          shapeProps.description = description;
        }

        // Copy any other custom properties from effective data
        // (excluding structural/positional properties)
        const excludedKeys = new Set([
          'id',
          'Name',
          'name',
          'title',
          'fillColor',
          'FillColor',
          'strokeColor',
          'StrokeColor',
          'textColor',
          'TextColor',
          'description',
          'Description',
          'x',
          'y',
          'width',
          'height',
          'w',
          'h',
          'type',
          'category',
          'zIndex',
          'locked',
          'visible',
          'selected',
          'rotation',
        ]);
        for (const [key, value] of Object.entries(effectiveData)) {
          if (!excludedKeys.has(key) && value !== undefined) {
            newShape[key] = value;
            shapeAttrs[key] = value;
            shapeProps[key] = value;
          }
        }

        shapes.create(newShape);
      }, 'drop-repository-item');

      canvasLogger.debug('Created bound shape from repository item', { shapeId, recordId: repoItem.recordId });
    },
    [doc],
  );

  const handleDragOver = (e: DragEvent) => {
    e.preventDefault();
    e.dataTransfer.dropEffect = 'copy';
  };

  const handleDrop = (e: DragEvent) => {
    e.preventDefault();

    // Check write permissions before allowing shape creation
    if (!canWrite) {
      return;
    }

    const stage = stageRef.current;
    if (!stage) return;

    // Get drop position in canvas coordinates
    const stageBox = stage.container().getBoundingClientRect();
    const x = (e.clientX - stageBox.left - stage.x()) / stage.scaleX();
    const y = (e.clientY - stageBox.top - stage.y()) / stage.scaleY();

    // Compass folder-resources subtree drop — a branch (account/VPC/
    // subnet/type-group) or a single leaf from `CompassFolderResourcesSection`.
    // The helper plans frames, shape positions, and edge wiring; we
    // materialise the plan as commands under one composite so the whole
    // drop is one undo step.
    const compassSubtreeData = e.dataTransfer.getData(COMPASS_SUBTREE_MIME);
    if (compassSubtreeData) {
      try {
        const payload = JSON.parse(compassSubtreeData) as CompassSubtreeDragPayload;
        if (Array.isArray(payload?.resources) && payload.resources.length > 0) {
          let idCounter = 0;
          const idFactory = () =>
            `compass-drop-${Date.now().toString(36)}-${(idCounter++).toString(36)}`;
          const plan = layoutDroppedSubtree(payload, idFactory);

          const inner: ICommand[] = [];

          // ── Architectural decision: containers are SHAPES, not frames ──
          //
          // VPC / Subnet / Account are first-class AWS resources with
          // structured props (CIDR, tags, route tables, encryption
          // flags, flow logs, etc.). Using a generic Creately frame
          // would strip all that — frames are purely visual grouping
          // and can't participate in findings rules, drift detection,
          // property-panel editing, graph provenance, or cross-diagram
          // federation. This is the same reason BPMN models Pool/Lane
          // as first-class shapes rather than frames.
          //
          // Container shapes already render with a dashed border and
          // an accent header (see `aws-shapes.manifest.ts`
          // `isContainer` branch). Trade-off: the header scales with
          // zoom whereas a frame pill stays zoom-stable. Worth it.
          // Follow-up (noted in impl plan): add a Konva-overlay
          // zoom-stable label to container shapes so we get both
          // domain semantics *and* the readable-at-any-zoom label.
          for (const frame of plan.frames) {
            const manifest =
              frame.kind === 'account'
                ? 'aws_account'
                : frame.kind === 'subnet'
                  ? 'aws_subnet'
                  : 'aws_vpc';
            const glyph =
              frame.kind === 'account' ? 'A' : frame.kind === 'subnet' ? 'S' : 'V';
            const accent =
              frame.kind === 'subnet'
                ? /public/i.test(frame.label)
                  ? '#00A4A6'
                  : '#248AED'
                : frame.kind === 'account'
                  ? '#232F3E'
                  : '#7E3DDF';
            inner.push(
              new CreateNetworkResourceCommand({
                id: frame.id,
                type: manifest,
                x: x + frame.x,
                y: y + frame.y,
                width: frame.width,
                height: frame.height,
                text: frame.label,
                attrs: {
                  name: frame.label,
                  resourceType:
                    frame.kind === 'account'
                      ? 'account'
                      : frame.kind === 'subnet'
                        ? 'subnet'
                        : 'vpc',
                  accentColor: accent,
                  strokeColor: accent,
                  isContainer: true,
                  containerLabel: frame.label,
                  containerGlyph: glyph,
                  compassBranchKind: frame.kind,
                  sourceFolderId: payload.sourceFolderId,
                  // Parent linkage — drives `useContainerChildrenDragSync`
                  // so dragging a VPC cascades its subnets + resources.
                  compassParentId: frame.parentFrameId,
                },
              }),
            );
          }

          for (const shape of plan.shapes) {
            inner.push(
              new CreateNetworkResourceCommand({
                id: shape.newId,
                type: shape.manifestId,
                x: x + shape.x,
                y: y + shape.y,
                width: shape.width,
                height: shape.height,
                text: shape.label,
                attrs: {
                  ...shape.props,
                  name: shape.label,
                  compassGraphNodeId: shape.sourceId,
                  sourceFolderId: payload.sourceFolderId,
                  compassParentId: shape.parentFrameId,
                },
              }),
            );
          }

          for (const edge of plan.edges) {
            inner.push(
              new CreateEdgeCommand({
                id: edge.id,
                type: 'line',
                from: edge.fromShapeId,
                to: edge.toShapeId,
                category: 'network',
                attrs: {
                  compassEdgeType: edge.type,
                  sourceFolderId: payload.sourceFolderId,
                },
              }),
            );
          }

          const label = `Drop ${payload.branchLabel} (${plan.shapes.length} resources, ${plan.edges.length} edges)`;
          void commandManager.execute(new CompositeCommand(label, inner));
          return;
        }
      } catch {
        // Malformed payload — fall through to generic handlers.
      }
    }

    // Check for repository item drop (from ProcessRepositoryPanel)
    const repoItemData = e.dataTransfer.getData('application/json');
    if (repoItemData) {
      try {
        const repoItem = JSON.parse(repoItemData);
        if (
          repoItem.type === 'process-repository-item' ||
          repoItem.type === 'process-graph-item'
        ) {
          handleRepositoryItemDrop(x, y, repoItem);
          return;
        }
        if (repoItem.type === 'orgchart-repository-item') {
          handleRepositoryItemDrop(x, y, repoItem);
          return;
        }
      } catch {
        // Not valid JSON or not a repo item - fall through to shape picker handling
      }
    }

    if (onImageFilesDrop) {
      const { files, skippedOverSize, skippedOverMaxCount } = collectSupportedImageFilesForCanvasDrop(
        e.dataTransfer,
      );
      if (files.length > 0 || skippedOverSize > 0 || skippedOverMaxCount > 0) {
        void onImageFilesDrop(files, { x, y }, { skippedOverSize, skippedOverMaxCount });
        return;
      }
      if (onPdfFilesDrop) {
        const { files: pdfFiles, skippedOverMaxCount: skippedPdfOverMaxCount } = collectSupportedPdfFilesForCanvasDrop(
          e.dataTransfer,
        );
        if (pdfFiles.length > 0 || skippedPdfOverMaxCount > 0) {
          void onPdfFilesDrop(pdfFiles, { x, y }, { skippedOverMaxCount: skippedPdfOverMaxCount });
          return;
        }
      }
      const hasDroppedFiles = (e.dataTransfer.files?.length ?? 0) > 0
        || Array.from(e.dataTransfer.items ?? []).some((item) => item.kind === 'file');
      if (hasDroppedFiles) {
        onUnsupportedImageDrop?.();
        return;
      }
    } else if (onImageFileDrop) {
      const droppedImage = extractFirstSupportedImageFileFromDataTransfer(e.dataTransfer);
      if (droppedImage) {
        void onImageFileDrop(droppedImage, { x, y });
        return;
      }
      const hasDroppedFiles = (e.dataTransfer.files?.length ?? 0) > 0
        || Array.from(e.dataTransfer.items ?? []).some((item) => item.kind === 'file');
      if (hasDroppedFiles) {
        onUnsupportedImageDrop?.();
        return;
      }
    }

    if (onImageUrlDrop) {
      const droppedImageUrl = extractImageUrlFromDataTransfer(e.dataTransfer);
      if (droppedImageUrl) {
        void onImageUrlDrop(droppedImageUrl, { x, y });
        return;
      }
    }

    const data = e.dataTransfer.getData('application/x-shape-picker');
    if (!data) return;

    try {
      const shapeData = JSON.parse(data);

      // If this is a table template schema, create from schema at drop position
      if (shapeData.category === 'tables' && typeof shapeData.schemaId === 'string') {
        const cmd = createTableFromSchema(shapeData.schemaId as string, { x, y });
        void commandManager.execute(cmd);
        return;
      }

      // Line shape: create as edge with free endpoints (same as click-to-add)
      if ((shapeData.shapeId === 'line' || shapeData.id === 'line') && shapeData.category !== 'tables') {
        const id = `edge-${Date.now()}-${Math.random().toString(36).slice(2, 9)}`;
        const fromPoint = { x: x - 50, y };
        const toPoint = { x: x + 50, y };
        const cmd = new CreateEdgeCommand({
          id,
          type: 'line',
          from: `__freeF:${id}`,
          to: `__freeT:${id}`,
          category: 'basic',
          attrs: { fromPoint, toPoint, routingType: 'straight' },
          version: 1,
        });
        void commandManager.execute(cmd);
        try {
          useDiagramStore.getState().setSelected(new Set([id]));
        } catch {
          // Ignore selection sync errors during freehand drop completion.
        }
        return;
      }
      
      const diagramType: DiagramType = (() => {
        if (
          shapeData.category === 'orgchart' ||
          shapeData.category === 'org' ||
          (typeof shapeData.shapeId === 'string' && shapeData.shapeId.startsWith('org_'))
        ) return 'org';
        if (shapeData.category === 'flowchart') return 'flowchart';
        if (shapeData.category === 'bpmn') return 'flowchart';
        if (shapeData.category === 'network') return 'network';
        return 'genogram';
      })();

      const manifestShapeId = (() => {
        if (shapeData.category === 'genogram' || shapeData.category === 'gender') return 'person';
        if (shapeData.category === 'text' || shapeData.shapeId === 'text') return 'text';
        if (shapeData.category === 'sticky-note' || shapeData.shapeId === 'sticky-note') return 'sticky-note';
        if (shapeData.shapeId === 'table') return 'table';
        return shapeData.shapeId;
      })();

      const manifest = getShapeManifest(diagramType, manifestShapeId);
      const orgDefaultDims = diagramType === 'org' && typeof manifestShapeId === 'string'
        ? resolveOrgShapeDefaultDimensions(manifestShapeId, resolveOrgViewMode(doc))
        : null;
      const defaultDims = orgDefaultDims || manifest?.defaultDimensions || { width: 80, height: 80 };

      // Network drops go through the network command factory so leaf
      // resources remain scenario-recordable resource nodes while
      // container manifests (VPC/Subnet/VNet/etc.) become Frames.
      if (shapeData.category === 'network') {
        const cmd = createNetworkElementCommand({
          type: shapeData.shapeId,
          x,
          y,
          width: defaultDims.width,
          height: defaultDims.height,
          text: typeof shapeData.text === 'string'
            ? shapeData.text
            : typeof shapeData.label === 'string'
              ? shapeData.label
              : undefined,
        });
        void commandManager.execute(cmd);
      } else {
        // Create shape at drop position
        // Align drop-created size with click placement default from ShapePickerTool
        const cmd = new CreateShapeCommand({
          type: shapeData.shapeId,
          x,
          y,
          width: defaultDims.width,
          height: defaultDims.height,
          text: typeof shapeData.text === 'string' ? shapeData.text : undefined,
          category: shapeData.category,
          ydoc: doc ?? undefined, // Pass Yjs document for persistence
        });
        commandManager.execute(cmd);
      }

      // Deactivate pen tool when a shape is dropped
      try {
        const activeTool = toolRegistry.getActiveTool();
        if (activeTool?.manifest.id === 'pen') {
          toolRegistry.activateTool('genogram-selection');
        }
      } catch {
        // Ignore tool deactivation errors during drag-drop completion.
      }
    } catch (err) {
      canvasLogger.error('Failed to handle drop', err);
    }
  };

  return { handleDragOver, handleDrop };
}

// NOTE: Earlier iterations emitted containers as generic Creately
// frames to get a zoom-stable label pill. That stripped AWS domain
// semantics (props, findings, drift, provenance). We reverted to
// container shapes (above) and implemented the zoom-stable pill as a
// shape-side Konva overlay — see
// `src/components/canvas/ContainerShapeLabelOverlay.tsx`.
